import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';

import { ApiClient } from '../../../../api-client';
import { Session } from '../../../../core/user/users.types';
import * as localStorage from '../../../../utils/storage/local';
import { useFetchUserSessionForNativeAuthentication } from '../../api';
import { isLoggedInAtom } from '../../atoms';
import { CoreAuthProviderProps } from '../../types';
import { logoutNative } from '../../utils';

const LEGACY_SESSION_KEY = 'gb_session';

/** Refreshes the session and syncs the session to the native client on update.  */
const NativeLegacySessionRefreshProvider = ({
  children,
  showLoader,
  LoaderComponent,
}: CoreAuthProviderProps) => {
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const hasFetchedUser = useRef(false);

  const [defaultStore] = useState(() => getDefaultStore());

  const { mutateAsync: fetchUserAsync } = useFetchUserSessionForNativeAuthentication();

  const fetchUser = useCallback(async () => {
    const nativeSession = localStorage.getItem(LEGACY_SESSION_KEY, 'json') as Session | null;
    if (!nativeSession) {
      throw new Error('No session found in native storage');
    }

    return await fetchUserAsync({
      secret: nativeSession.secret,
      token: nativeSession.token,
    });
  }, [fetchUserAsync]);

  // Get the session from the native storage and refresh the session.
  // This is done to ensure that the session api client
  useEffect(() => {
    const updateSession = async () => {
      try {
        void (await queryClient.fetchQuery({
          queryKey: ['user'],
          queryFn: fetchUser,
        }));

        defaultStore.set(isLoggedInAtom, true);
      } catch {
        logoutNative();
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetchedUser.current) {
      void updateSession();
      hasFetchedUser.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = ApiClient.onRefreshToken((response) => {
      if ('error' in response) {
        logoutNative();
      }
    });
    return () => void unsubscribe();
  }, []);

  if (loading && showLoader) {
    return <LoaderComponent visible={true} />;
  }
  return children;
};

export { NativeLegacySessionRefreshProvider };
